import { WSFilterOption } from "@wingspanhq/fe-component-library";
import {
  PayableStatus,
  PayerApprovalStatus,
  SearchPayableRowsFilterParameter
} from "@wingspanhq/wingspan-api-sdk";

export const STATUS_FILTER_ALL = "All";

export type PayablesStatusFilterValue =
  | typeof STATUS_FILTER_ALL
  | "draft"
  | "pendingApproval"
  | "eligibilityPending"
  | "scheduled"
  | "paid"
  | "overdue"
  | "rejected";

type PayablesStatusFilterKey = Exclude<
  PayablesStatusFilterValue,
  typeof STATUS_FILTER_ALL
>;

/**
 * The `searchPayableRows` fields a status option constrains. There is no single
 * v3 payable status behind the payer-facing states: each one is a compound of
 * the raw `PayableStatus` lifecycle and the `PayerApprovalStatus` workflow, so
 * an option always sets both (`undefined` clears the tab's own constraint).
 */
type StatusClause = Pick<
  SearchPayableRowsFilterParameter,
  "status" | "payerApprovalStatus"
>;

const NOT_YET_APPROVED = [
  PayerApprovalStatus.PENDING,
  PayerApprovalStatus.PRE_APPROVED
];

/**
 * Excludes the payables the payer has rejected. Shared with the dashboard
 * summary tiles: the same label reported two populations while only one surface
 * carried the clause (PLA-3750).
 *
 * `Declined` survives a contractor resubmission (PLA-2847), which this cannot
 * re-admit — one request cannot OR `payerApprovalStatus` against
 * `payeeReviewStatus`.
 */
export const PAYABLES_NOT_DECLINED_CLAUSE: StatusClause["payerApprovalStatus"] =
  {
    noneOf: [PayerApprovalStatus.DECLINED]
  };

// Overdue overlaps the workflow statuses and adds a due-date constraint in createSearchRequest.
export const PAYABLES_STATUS_CLAUSES: Record<
  PayablesStatusFilterKey,
  StatusClause
> = {
  overdue: {
    status: { anyOf: [PayableStatus.OPENED] },
    payerApprovalStatus: PAYABLES_NOT_DECLINED_CLAUSE
  },
  draft: {
    status: { anyOf: [PayableStatus.CREATED] },
    payerApprovalStatus: undefined
  },
  pendingApproval: {
    status: { anyOf: [PayableStatus.OPENED] },
    payerApprovalStatus: { anyOf: NOT_YET_APPROVED }
  },
  eligibilityPending: {
    status: { anyOf: [PayableStatus.PENDING] },
    payerApprovalStatus: { anyOf: NOT_YET_APPROVED }
  },
  scheduled: {
    status: { anyOf: [PayableStatus.PENDING, PayableStatus.OPENED] },
    payerApprovalStatus: { anyOf: [PayerApprovalStatus.APPROVED] }
  },
  paid: {
    status: {
      anyOf: [PayableStatus.PAID, PayableStatus.PAYMENT_IN_TRANSIT]
    },
    payerApprovalStatus: undefined
  },
  rejected: {
    status: { anyOf: [PayableStatus.OPENED, PayableStatus.PENDING] },
    payerApprovalStatus: { eq: PayerApprovalStatus.DECLINED }
  }
};

export const PAYABLES_STATUS_OPTIONS: WSFilterOption<"radio">[] = [
  { name: STATUS_FILTER_ALL, label: "All", hidePill: true },
  { name: "draft", label: "Draft" },
  { name: "pendingApproval", label: "Pending approval" },
  { name: "eligibilityPending", label: "Payments eligibility pending" },
  { name: "scheduled", label: "Scheduled" },
  { name: "paid", label: "Paid" },
  { name: "overdue", label: "Overdue" },
  { name: "rejected", label: "Rejected" }
];

export const isActiveStatusFilter = (
  value: PayablesStatusFilterValue | undefined
): value is PayablesStatusFilterKey => !!value && value !== STATUS_FILTER_ALL;
